import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Col, Row, Space, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { EMachineTranslationStatus, ETranslationFlag } from "@devowl-wp/api";
import { TemplateMetaDataField, getTemplateHeadline } from "@devowl-wp/api-real-cookie-banner";
import { TemplateDiff } from "./template-diff.js";
import { TranslationInfoStateChange } from "./translation-info-state-change.js";
import { TranslationTagsRow, TranslationTagsRowWithHover } from "./translation-tags-row.js";
import { useContentBlockerTemplateById } from "../../../../../hooks/content-blocker/useContentBlockerTemplate.js";
import { useServiceTemplateById } from "../../../../../hooks/service/useServiceTemplate.js";
import { useRelease } from "../../../../../hooks/useRelease.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { colors } from "../../../../../styles/colors.js";
import { spacing } from "../../../../../styles/spacing.js";
import { RELEASE_HIDDEN_DIFF_PROPS } from "../../../../../types/template/release.js";
import { removePropertiesByName } from "../../../../../util/object.js";
import { QueryKeys } from "../../../../../util/query-keys.js";
import { getReleaseValidationExpandable } from "../../../../../util/release-validation.js";
import { calculateTemplateMetaDataVersionUpdateStateForTemplate } from "../../../../../util/release-version-state.js";
import { EditModal } from "../../../../layout/edit-modal.js";
import { ContentBlockerTemplateEdit } from "../../template/content-blocker/contentBlockerTemplateEdit.js";
import { useLanguageOptions } from "../../template/fields/translation/language-select.js";
import { TemplateMetaDataVersionUpdate } from "../../template/fields/version-update.js";
import { TemplateMetaDataTable } from "../../template/meta-data/templateMetaDataTable.js";
import { ServiceTemplateEdit } from "../../template/service/serviceTemplateEdit.js";
import { UserTagLastEditors } from "../../user/user-tag-last-editors.js";
import { ReleaseValidationNotice } from "../release-validation-notice.js";
const DEFAULT_ROW_SELECTIONS = [
    Table.SELECTION_ALL,
    Table.SELECTION_INVERT,
    Table.SELECTION_NONE
];
function ReleaseExpansion(params) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const languages = useLanguageOptions(t);
    const { release, rowSelectionConfigContentBlockers, rowSelectionConfigServices, releaseCheck } = params;
    const { dependencies, serviceValidations, contentBlockerValidations } = releaseCheck || {};
    const { data: releaseData, isFetched: releaseIsFetched, isFetching: releaseIsFetching } = useRelease(release.id);
    const [diffIsOpen, setDiffIsOpen] = useState(false);
    const [left, setLeft] = useState("");
    const [right, setRight] = useState("");
    const [title, setTitle] = useState("");
    const resetCompareValues = ()=>{
        setLeft("");
        setRight("");
        setLeftServiceId(null);
        setRightServiceId(null);
        setLeftBlockerId(null);
        setRightBlockerId(null);
    };
    const [leftServiceId, setLeftServiceId] = useState(null);
    const [rightServiceId, setRightServiceId] = useState(null);
    const leftServiceQuery = useServiceTemplateById(leftServiceId);
    const rightServiceQuery = useServiceTemplateById(rightServiceId);
    const [leftBlockerId, setLeftBlockerId] = useState(null);
    const [rightBlockerId, setRightBlockerId] = useState(null);
    const leftBlockerQuery = useContentBlockerTemplateById(leftBlockerId);
    const rightBlockerQuery = useContentBlockerTemplateById(rightBlockerId);
    useEffect(()=>{
        if (leftServiceQuery.data) {
            setLeft(JSON.stringify(removePropertiesByName(leftServiceQuery.data, RELEASE_HIDDEN_DIFF_PROPS), null, 2));
        }
        if (rightServiceQuery.data) {
            setRight(JSON.stringify(removePropertiesByName(rightServiceQuery.data, RELEASE_HIDDEN_DIFF_PROPS), null, 2));
            setTitle(t(`Changes to ${rightServiceQuery.data?.name}`));
        }
    }, [
        leftServiceQuery,
        rightServiceQuery
    ]);
    useEffect(()=>{
        if (leftBlockerQuery.data) {
            setLeft(JSON.stringify(removePropertiesByName(leftBlockerQuery.data, RELEASE_HIDDEN_DIFF_PROPS), null, 2));
        }
        if (rightBlockerQuery.data) {
            setRight(JSON.stringify(removePropertiesByName(rightBlockerQuery.data, RELEASE_HIDDEN_DIFF_PROPS), null, 2));
            setTitle(t(`Changes to ${rightBlockerQuery.data?.name}`));
        }
    }, [
        leftBlockerQuery,
        rightBlockerQuery
    ]);
    const [serviceTranslationStatus, setServiceTranslationStatus] = useState([]);
    const [servicesIncomplete, setServicesIncomplete] = useState([]);
    const [servicesFuzzy, setServicesFuzzy] = useState([]);
    const [servicesPartialMachineTranslated, setServicesPartialMachineTranslated] = useState([]);
    const [blockerTranslationStatus, setBlockerTranslationStatus] = useState([]);
    const [blockersIncomplete, setBlockersIncomplete] = useState([]);
    const [blockersFuzzy, setBlockersFuzzy] = useState([]);
    const [blockersPartialMachineTranslated, setBlockersPartialMachineTranslated] = useState([]);
    const [serviceTemplates, setServiceTemplates] = useState([]);
    const [contentBlockerTemplates, setContentBlockerTemplates] = useState([]);
    useEffect(()=>{
        if (releaseData) {
            const services = releaseData?.current?.serviceTemplates;
            const blockers = releaseData?.current?.contentBlockerTemplates;
            const prevServices = releaseData?.prev?.serviceTemplates;
            const prevBlockers = releaseData?.prev?.contentBlockerTemplates;
            // calculate temporary release state
            services?.filter((service)=>calculateTemplateMetaDataVersionUpdateStateForTemplate(service, prevServices));
            blockers?.filter((blocker)=>calculateTemplateMetaDataVersionUpdateStateForTemplate(blocker, prevBlockers));
            setServiceTemplates(services);
            setContentBlockerTemplates(blockers);
            // reset translation state
            setServicesIncomplete([]);
            setServicesFuzzy([]);
            setServicesPartialMachineTranslated([]);
            setBlockersIncomplete([]);
            setBlockersFuzzy([]);
            setBlockersPartialMachineTranslated([]);
            setServiceTranslationStatus(services?.map((currTemplate)=>{
                const isCurrComplete = isTranslationInfoCompleteOverall(currTemplate.translationInfo);
                const isCurrentRemoved = currTemplate.isDeleted; // a removed template counts as completed
                const prevFound = releaseData.prev.serviceTemplates?.find((prevTemplate)=>prevTemplate.identifier === currTemplate.identifier);
                const isPrevComplete = isTranslationInfoCompleteOverall(prevFound?.translationInfo);
                const isRelevantDependency = dependencies ? dependencies.serviceIds?.includes(String(currTemplate.id)) : true;
                // if the template is not removed and is a relevant dependency, we need to check it
                if (!isCurrentRemoved && isRelevantDependency) {
                    if (!isCurrComplete && isPrevComplete) {
                        if (currTemplate?.headline?.length > 0) {
                            setServicesIncomplete((prev)=>{
                                return [
                                    ...prev,
                                    getTemplateHeadline(currTemplate, true, false)
                                ];
                            });
                        } else {
                            setServicesIncomplete((prev)=>{
                                return [
                                    ...prev,
                                    getTemplateHeadline(prevFound, true, false)
                                ];
                            });
                        }
                    }
                    const fuzzyInfos = getAnyTranslationFlaggedInfos(currTemplate.translationInfo, ETranslationFlag.Fuzzy);
                    if (fuzzyInfos?.length > 0) {
                        setServicesFuzzy((prev)=>{
                            return [
                                ...prev,
                                {
                                    text: getTemplateHeadline(currTemplate, true, false),
                                    hoverContent: /*#__PURE__*/ _jsx(Space, {
                                        size: "small",
                                        children: fuzzyInfos.map((info)=>languages.find((lang)=>lang.value === info.language)?.icon)
                                    })
                                }
                            ];
                        });
                    }
                    const partlyMachineTranslatedInfos = getPartlyMachineTranslatedInfos(currTemplate.translationInfo);
                    if (partlyMachineTranslatedInfos?.length > 0) {
                        setServicesPartialMachineTranslated((prev)=>{
                            return [
                                ...prev,
                                {
                                    text: getTemplateHeadline(currTemplate, true, false),
                                    hoverContent: /*#__PURE__*/ _jsx(Space, {
                                        size: "small",
                                        children: partlyMachineTranslatedInfos.map((info)=>languages.find((lang)=>lang.value === info.language)?.icon)
                                    })
                                }
                            ];
                        });
                    }
                }
                return {
                    identifier: currTemplate.identifier,
                    previousComplete: isPrevComplete,
                    currentComplete: isCurrentRemoved || isCurrComplete
                };
            }));
            setBlockerTranslationStatus(blockers?.map((currTemplate)=>{
                const isCurrComplete = isTranslationInfoCompleteOverall(currTemplate.translationInfo);
                const isCurrentRemoved = currTemplate.isDeleted; // a removed template counts as completed
                const prevFound = releaseData.prev.contentBlockerTemplates?.find((prevTemplate)=>prevTemplate.identifier === currTemplate.identifier);
                const isPrevComplete = isTranslationInfoCompleteOverall(prevFound?.translationInfo);
                const isRelevantDependency = dependencies ? dependencies.contentBlockerIds?.includes(String(currTemplate.id)) : true;
                // if the template is not removed and is a relevant dependency, we need to check it
                if (!isCurrentRemoved && isRelevantDependency) {
                    if (!isCurrComplete && isPrevComplete) {
                        if (currTemplate?.headline?.length > 0) {
                            setBlockersIncomplete((prev)=>{
                                return [
                                    ...prev,
                                    getTemplateHeadline(currTemplate, true, false)
                                ];
                            });
                        } else {
                            setBlockersIncomplete((prev)=>{
                                return [
                                    ...prev,
                                    getTemplateHeadline(prevFound, true, false)
                                ];
                            });
                        }
                    }
                    const fuzzyInfos = getAnyTranslationFlaggedInfos(currTemplate.translationInfo, ETranslationFlag.Fuzzy);
                    if (fuzzyInfos?.length > 0) {
                        setBlockersFuzzy((prev)=>{
                            return [
                                ...prev,
                                {
                                    text: getTemplateHeadline(currTemplate, true, false),
                                    hoverContent: /*#__PURE__*/ _jsx(Space, {
                                        size: "small",
                                        children: fuzzyInfos.map((info)=>languages.find((lang)=>lang.value === info.language)?.icon)
                                    })
                                }
                            ];
                        });
                    }
                    const partlyMachineTranslatedInfos = getPartlyMachineTranslatedInfos(currTemplate.translationInfo);
                    if (partlyMachineTranslatedInfos?.length > 0) {
                        setBlockersPartialMachineTranslated((prev)=>{
                            return [
                                ...prev,
                                {
                                    text: getTemplateHeadline(currTemplate, true, false),
                                    hoverContent: /*#__PURE__*/ _jsx(Space, {
                                        size: "small",
                                        children: partlyMachineTranslatedInfos.map((info)=>languages.find((lang)=>lang.value === info.language)?.icon)
                                    })
                                }
                            ];
                        });
                    }
                }
                return {
                    identifier: currTemplate.identifier,
                    previousComplete: isPrevComplete,
                    currentComplete: isCurrentRemoved || isCurrComplete
                };
            }));
            // If any dependencies are given, filter them.
            if (dependencies) {
                const { contentBlockerIds, serviceIds } = dependencies;
                const dependencyServices = services.filter((template)=>serviceIds.includes(String(template.id)));
                setServiceTemplates(dependencyServices);
                const dependencyBlockers = blockers.filter((template)=>contentBlockerIds.includes(String(template.id)));
                setContentBlockerTemplates(dependencyBlockers);
                if (serviceValidations || contentBlockerValidations) {
                    const expandedServiceIds = serviceValidations?.map((t)=>t.id) || [];
                    setExpandedServiceIds([
                        ...new Set([
                            ...expandedServiceIds.filter((id)=>serviceIds.includes(id))
                        ])
                    ]);
                    const expandedBlockerIds = contentBlockerValidations?.map((t)=>t.id) || [];
                    setExpandedBlockerIds([
                        ...new Set([
                            ...expandedBlockerIds.filter((id)=>contentBlockerIds.includes(id))
                        ])
                    ]);
                }
            }
        }
    }, [
        releaseData,
        dependencies
    ]);
    const [expandedServiceIds, setExpandedServiceIds] = useState(undefined);
    const [expandedBlockerIds, setExpandedBlockerIds] = useState(undefined);
    // edit model
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editOpenType, setEditOpenType] = useState("Service");
    const [editIdentifier, setEditIdentifier] = useState(undefined);
    const openFullScreen = (type, identifier)=>{
        setEditModalOpen(true);
        setEditOpenType(type);
        setEditIdentifier(identifier);
    };
    const onAfterSave = ()=>{
        setEditModalOpen(false);
        if (release.id === "") {
            queryClient.invalidateQueries(QueryKeys.getReleaseById(release.id));
        }
    };
    const [serviceExpandable, setServiceExpandable] = useState(undefined);
    const [blockerExpandable, setBlockerExpandable] = useState(undefined);
    useEffect(()=>{
        const serviceExpandable = getReleaseValidationExpandable(serviceValidations, expandedServiceIds?.length > 0, expandedServiceIds);
        setServiceExpandable(serviceExpandable);
        const contentBlockerExpandable = getReleaseValidationExpandable(contentBlockerValidations, expandedBlockerIds?.length > 0, expandedBlockerIds);
        setBlockerExpandable(contentBlockerExpandable);
    }, [
        serviceTemplates,
        contentBlockerTemplates,
        expandedServiceIds,
        expandedBlockerIds
    ]);
    const rowSelectionServices = rowSelectionConfigServices ? {
        selectedRowKeys: rowSelectionConfigServices.selectedRowKeys,
        onChange: rowSelectionConfigServices.onChange,
        selections: rowSelectionConfigServices.selections || DEFAULT_ROW_SELECTIONS
    } : undefined;
    const rowSelectionContentBlockers = rowSelectionConfigContentBlockers ? {
        selectedRowKeys: rowSelectionConfigContentBlockers.selectedRowKeys,
        onChange: rowSelectionConfigContentBlockers.onChange,
        selections: rowSelectionConfigContentBlockers.selections || DEFAULT_ROW_SELECTIONS
    } : undefined;
    const hiddenColumns = [
        TemplateMetaDataField.Status
    ];
    if (release?.id !== "") {
        // hide if its not the create release view
        hiddenColumns.push("lastEditorsSinceRelease");
    }
    const { Title } = Typography;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(Col, {
                    style: {
                        flex: 1,
                        padding: spacing.layout.content.paddingSmall
                    },
                    children: /*#__PURE__*/ _jsx(TemplateMetaDataTable, {
                        rowSelection: rowSelectionServices,
                        hiddenColumns: hiddenColumns,
                        extraColumns: [
                            {
                                title: t("Release Type"),
                                dataIndex: "releaseStatus",
                                render: (status)=>{
                                    return /*#__PURE__*/ _jsx(TemplateMetaDataVersionUpdate, {
                                        status: status
                                    });
                                }
                            },
                            {
                                title: t("Translation State"),
                                render: (_, record)=>{
                                    const foundStatus = serviceTranslationStatus?.find((status)=>status.identifier === record.identifier);
                                    return /*#__PURE__*/ _jsx(TranslationInfoStateChange, {
                                        stateChange: foundStatus
                                    });
                                },
                                align: "center"
                            },
                            {
                                title: t("Edits"),
                                dataIndex: "lastEditorsSinceRelease",
                                render: (_, record)=>{
                                    return /*#__PURE__*/ _jsx(UserTagLastEditors, {
                                        lastKnownEditor: record.lastEditedBy,
                                        templateId: record.id,
                                        type: "Service",
                                        disabled: !record.lastEditedBy
                                    });
                                },
                                width: "5rem"
                            }
                        ],
                        loading: releaseIsFetching,
                        tableTitle: {
                            title: t("Services ({{count}})", {
                                count: rowSelectionServices ? rowSelectionServices.selectedRowKeys.length : serviceTemplates?.length || 0
                            }),
                            props: {
                                level: 5
                            }
                        },
                        size: "small",
                        tableInfoNotice: releaseIsFetched ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(ReleaseValidationNotice, {
                                    hidden: !expandedServiceIds || expandedServiceIds?.length === 0
                                }),
                                servicesIncomplete?.length > 0 || servicesFuzzy?.length > 0 || servicesPartialMachineTranslated?.length > 0 ? /*#__PURE__*/ _jsx(Alert, {
                                    message: /*#__PURE__*/ _jsx(Title, {
                                        level: 5,
                                        children: t("Translation state warnings")
                                    }),
                                    type: "warning",
                                    style: {
                                        padding: spacing.layout.content.paddingSmall
                                    },
                                    description: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(TranslationTagsRow, {
                                                title: t("Incomplete"),
                                                flags: servicesIncomplete,
                                                tagOptions: {
                                                    color: colors.translation.incomplete
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationTagsRowWithHover, {
                                                title: t("Fuzzy"),
                                                flags: servicesFuzzy,
                                                tagOptions: {
                                                    color: colors.translation.fuzzy
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationTagsRowWithHover, {
                                                title: t("Partial machine-Translated"),
                                                flags: servicesPartialMachineTranslated,
                                                tagOptions: {
                                                    color: colors.translation.machineTranslated
                                                }
                                            })
                                        ]
                                    })
                                }) : null
                            ]
                        }) : null,
                        templateMetaData: serviceTemplates,
                        pagination: {
                            defaultPageSize: 10,
                            showSizeChanger: true
                        },
                        actions: {
                            onDiff: (template)=>{
                                resetCompareValues();
                                setRightServiceId(template.id);
                                setLeftServiceId(releaseData?.prev?.serviceTemplates?.find((t)=>t.identifier === template.identifier)?.id || null);
                                setDiffIsOpen(true);
                            },
                            onDiffTitle: t("Compare"),
                            onEdit: !releaseCheck ? (template)=>{
                                openFullScreen("Service", template.identifier);
                            } : undefined
                        },
                        expandable: serviceExpandable
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(Col, {
                    style: {
                        flex: 1,
                        padding: spacing.layout.content.paddingSmall
                    },
                    children: /*#__PURE__*/ _jsx(TemplateMetaDataTable, {
                        rowSelection: rowSelectionContentBlockers,
                        hiddenColumns: hiddenColumns,
                        extraColumns: [
                            {
                                title: t("Release Type"),
                                dataIndex: "releaseStatus",
                                render: (status)=>{
                                    return /*#__PURE__*/ _jsx(TemplateMetaDataVersionUpdate, {
                                        status: status
                                    });
                                }
                            },
                            {
                                title: t("Translation State"),
                                render: (_, record)=>{
                                    const foundStatus = blockerTranslationStatus?.find((status)=>status.identifier === record.identifier);
                                    return /*#__PURE__*/ _jsx(TranslationInfoStateChange, {
                                        stateChange: foundStatus
                                    });
                                },
                                align: "center"
                            },
                            {
                                title: t("Edits"),
                                dataIndex: "lastEditorsSinceRelease",
                                render: (_, record)=>{
                                    return /*#__PURE__*/ _jsx(UserTagLastEditors, {
                                        lastKnownEditor: record.lastEditedBy,
                                        templateId: record.id,
                                        type: "Content-Blocker",
                                        disabled: !record.lastEditedBy
                                    });
                                },
                                width: "5rem"
                            }
                        ],
                        loading: releaseIsFetching,
                        tableTitle: {
                            title: t("Content Blocker ({{count}})", {
                                count: rowSelectionContentBlockers ? rowSelectionContentBlockers.selectedRowKeys.length : contentBlockerTemplates?.length || 0
                            }),
                            props: {
                                level: 5
                            }
                        },
                        size: "small",
                        tableInfoNotice: releaseIsFetched ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(ReleaseValidationNotice, {
                                    hidden: !expandedBlockerIds || expandedBlockerIds?.length === 0
                                }),
                                blockersIncomplete?.length > 0 || blockersFuzzy?.length > 0 || blockersPartialMachineTranslated?.length > 0 ? /*#__PURE__*/ _jsx(Alert, {
                                    message: /*#__PURE__*/ _jsx(Title, {
                                        level: 5,
                                        children: t("Translation state warnings")
                                    }),
                                    type: "warning",
                                    style: {
                                        padding: spacing.layout.content.paddingSmall
                                    },
                                    description: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(TranslationTagsRow, {
                                                title: t("Incomplete"),
                                                flags: blockersIncomplete,
                                                tagOptions: {
                                                    color: colors.translation.incomplete
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationTagsRowWithHover, {
                                                title: t("Fuzzy"),
                                                flags: blockersFuzzy,
                                                tagOptions: {
                                                    color: colors.translation.fuzzy
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx(TranslationTagsRowWithHover, {
                                                title: t("Partial machine-Translated"),
                                                flags: blockersPartialMachineTranslated,
                                                tagOptions: {
                                                    color: colors.translation.machineTranslated
                                                }
                                            })
                                        ]
                                    })
                                }) : null
                            ]
                        }) : null,
                        templateMetaData: contentBlockerTemplates,
                        pagination: {
                            defaultPageSize: 10,
                            showSizeChanger: true
                        },
                        actions: {
                            onDiff: (template)=>{
                                resetCompareValues();
                                setRightBlockerId(template.id);
                                setLeftBlockerId(releaseData.prev?.contentBlockerTemplates?.find((t)=>t.identifier === template.identifier)?.id || null);
                                setDiffIsOpen(true);
                            },
                            onDiffTitle: t("Compare"),
                            onEdit: !releaseCheck ? (template)=>{
                                openFullScreen("Content-Blocker", template.identifier);
                            } : undefined
                        },
                        expandable: blockerExpandable
                    })
                })
            }),
            /*#__PURE__*/ _jsx(TemplateDiff, {
                title: title,
                open: diffIsOpen,
                left: left,
                right: right,
                onOk: ()=>setDiffIsOpen(false),
                onCancel: ()=>setDiffIsOpen(false),
                loading: leftServiceQuery.isFetching || rightServiceQuery.isFetching || leftBlockerQuery.isFetching || rightBlockerQuery.isFetching
            }),
            /*#__PURE__*/ _jsx(EditModal, {
                open: editModalOpen && editOpenType === "Service",
                onCancel: ()=>setEditModalOpen(false),
                footer: null,
                children: /*#__PURE__*/ _jsx(ServiceTemplateEdit, {
                    modalModeOptions: {
                        editIdentifier: editOpenType === "Service" ? editIdentifier : undefined,
                        onAfterSave
                    }
                })
            }),
            /*#__PURE__*/ _jsx(EditModal, {
                open: editModalOpen && editOpenType === "Content-Blocker",
                onCancel: ()=>setEditModalOpen(false),
                footer: null,
                children: /*#__PURE__*/ _jsx(ContentBlockerTemplateEdit, {
                    modalModeOptions: {
                        editIdentifier: editOpenType === "Content-Blocker" ? editIdentifier : undefined,
                        onAfterSave
                    }
                })
            })
        ]
    }, release.id);
}
/**
 * Checks if the translation information is complete overall.
 *
 * @param infos - Array of translation status objects.
 * @returns True if the translation information is complete overall, otherwise false.
 */ function isTranslationInfoCompleteOverall(infos) {
    const filteredInfos = infos?.filter((info)=>info.translatedOptionalFields.length + info.translatedRequiredFields.length > 1);
    const mappedInfos = (filteredInfos || []).map((info)=>{
        if (Object.hasOwn(info, "isTranslationCompleteRecursive")) {
            return info.isTranslationCompleteRecursive;
        } else {
            return info.isTranslationComplete;
        }
    });
    if (mappedInfos.length > 0) {
        return mappedInfos.reduce((prev, current)=>prev && current);
    }
    return true;
}
/**
 * Gets the translation infos that are flagged with the specified flag.
 *
 * @param infos - Array of translation status objects.
 * @param flag - The translation flag to check for.
 * @returns Array of translation status objects that are flagged with the specified flag.
 */ function getAnyTranslationFlaggedInfos(infos, flag) {
    const found = infos?.filter((info)=>{
        if (info.translationFlaggedFields) {
            switch(flag){
                case ETranslationFlag.Fuzzy:
                    return info.translationFlaggedFields.Fuzzy?.length > 0;
                case ETranslationFlag.MachineTranslated:
                    return info.translationFlaggedFields.MachineTranslated?.length > 0;
            }
        }
        return false;
    }) || [];
    return found;
}
/**
 * Gets the machine translated infos that are not fully machine translated.
 *
 * @param infos - Array of translation status objects.
 * @returns Array of translation status objects that are machine translated but not fully machine translated.
 */ function getPartlyMachineTranslatedInfos(infos) {
    return infos?.filter((info)=>info.machineTranslationStatus === EMachineTranslationStatus.Partly);
}
export { ReleaseExpansion, getPartlyMachineTranslatedInfos };
